import React, { useCallback } from 'react';
import { Flex } from '@televet/kibble-ui/build/chakra';
import {
  ChannelFilterSelectionFragment,
  ChannelStatusAction,
  useGetAllClinicChannelStatusesQuery,
  useGetChannelFilterSelectionsQuery,
} from 'shared/types/graphql';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import { IChannelListFilterSelection } from 'pages/Conversations/interfaces/IChannelListFilterSelection';
import ChannelListFilterEdit from './ChannelListFilterEdit';
import ChannelListFilterEditSaved from './ChannelListFilterEditSaved';
import ChannelListFilterSelect from './ChannelListFilterSelect';
import { IChannelListFilter } from 'pages/Conversations/interfaces/IChannelListFilter';
import { IFilterPopoverState, FilterPopoverView } from '../interfaces/IFilterPopoverState';
import { IFilterPopoverEditState } from '../interfaces/IFilterPopoverEditState';
import { convertPersistedFilterSelectionToDraft } from '../utils/convertPersistedFilterSelectionToDraft';
import useClinicUser from 'shared/hooks/useClinicUser';
import { useCustomToast } from 'shared/hooks/useCustomToast';

interface IChannelListFilterPopoverContentProps {
  filterPopoverState: IFilterPopoverState;
  filterPopoverEditState: IFilterPopoverEditState;
  currentChannelFilterSelection: IChannelListFilterSelection;
  initialFocusRef: React.MutableRefObject<HTMLDivElement | null>;
  onSetFilterPopoverState: React.Dispatch<React.SetStateAction<IFilterPopoverState>>;
  onSetFilterPopoverEditState: React.Dispatch<React.SetStateAction<IFilterPopoverEditState>>;
  onSelectFilter: (channelFilterSelection: IChannelListFilterSelection) => void;
  onEditFilterComplete: () => void;
  onClearFilter: (source: string, ignoreKeys?: (keyof IChannelListFilter)[]) => void;
  onDeleteFilter: (channelFilterSelection: ChannelFilterSelectionFragment) => void;
  onSaveNewFilter: () => void;
  onClosePopover: () => void;
  onShowArchivedToggle: () => void;
}

const ChannelListFilterPopoverContent = ({
  filterPopoverState,
  filterPopoverEditState,
  currentChannelFilterSelection,
  initialFocusRef,
  onSetFilterPopoverState,
  onSetFilterPopoverEditState,
  onSelectFilter,
  onEditFilterComplete,
  onClearFilter,
  onDeleteFilter,
  onSaveNewFilter,
  onClosePopover,
  onShowArchivedToggle,
}: IChannelListFilterPopoverContentProps): JSX.Element => {
  const { currentClinicId } = useClinicUser();
  const { toastMessagePop } = useCustomToast();

  const { data: channelFilterSelectionsData, loading: isChannelFilterSelectionsQueryLoading } =
    useGetChannelFilterSelectionsQuery({ fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork });

  const { data: channelStatusesData } = useGetAllClinicChannelStatusesQuery({
    fetchPolicy: GraphQLFetchPolicies.CacheFirst,
    variables: { clinicId: currentClinicId || '' },
    skip: !currentClinicId,
  });

  const handleClearFilterClick = useCallback(
    (source: string): void => {
      onClearFilter(source, ['unread']);
    },
    [onClearFilter],
  );

  const handleViewFilterEdit = useCallback((): void => {
    onSetFilterPopoverState((value) => ({
      ...value,
      view: FilterPopoverView.Edit,
    }));
  }, [onSetFilterPopoverState]);

  const handleViewFilterSelect = useCallback((): void => {
    onSetFilterPopoverState((value) => ({
      ...value,
      view: FilterPopoverView.Select,
    }));
  }, [onSetFilterPopoverState]);

  const handleViewFilterEditSaved = useCallback(
    (channelFilterSelection: ChannelFilterSelectionFragment) => {
      onSetFilterPopoverState((value) => ({
        ...value,
        view: FilterPopoverView.EditSaved,
      }));
      let showArchived = !!channelFilterSelection.filters?.showArchived;
      if (!showArchived && channelFilterSelection.filters?.channelStatusIds) {
        const hasArchivedStatus = (channelFilterSelection.filters.channelStatusIds as string[]).some((id) => {
          const channelStatus = channelStatusesData?.findManyChannelStatus?.find((status) => status.id === id);
          return channelStatus && channelStatus.channelStatusAction !== ChannelStatusAction.Active;
        });
        if (hasArchivedStatus) {
          showArchived = true;
        }
      }
      onSetFilterPopoverEditState((value) => ({
        ...value,
        channelFilterSelection,
        filterDraft: convertPersistedFilterSelectionToDraft({
          ...channelFilterSelection,
          filters: {
            ...channelFilterSelection.filters,
            showArchived,
          },
        }),
      }));
    },
    [channelStatusesData, onSetFilterPopoverState, onSetFilterPopoverEditState],
  );

  const handleSavedFilterShowArchivedToggle = useCallback(() => {
    onSetFilterPopoverEditState((value) => {
      const filters = value.filterDraft.filters;
      const showArchived = !filters.showArchived;
      let channelStatusIds = filters.channelStatusIds;
      if (!showArchived && Array.isArray(channelStatusIds) && channelStatusIds.length) {
        const archivedStatuses = new Set(
          channelStatusIds
            .filter(({ value }) => {
              const channelStatus = channelStatusesData?.findManyChannelStatus?.find((status) => status.id === value);
              return channelStatus && channelStatus.channelStatusAction !== ChannelStatusAction.Active;
            })
            .map(({ value }) => value),
        );
        channelStatusIds = channelStatusIds.filter(({ value }) => !archivedStatuses.has(value));
      }
      return {
        ...value,
        filterDraft: {
          ...value.filterDraft,
          filters: {
            ...value.filterDraft.filters,
            showArchived,
            channelStatusIds,
          },
        },
      };
    });
  }, [channelStatusesData, onSetFilterPopoverEditState]);

  const handleSelectFilterClick = useCallback(
    (channelFilterSelection: ChannelFilterSelectionFragment): void => {
      onSelectFilter(channelFilterSelection);
    },
    [onSelectFilter],
  );

  const handleFilterApply = useCallback(
    (channelFilterSelection: IChannelListFilterSelection): void => {
      onSelectFilter(channelFilterSelection);
    },
    [onSelectFilter],
  );

  const handleFilterUpdate = useCallback(
    (channelFilterSelection: ChannelFilterSelectionFragment): void => {
      if (channelFilterSelection.id === currentChannelFilterSelection.id) {
        handleSelectFilterClick(channelFilterSelection);
      }
      toastMessagePop('Changes saved');
      onEditFilterComplete();
    },
    [currentChannelFilterSelection.id, handleSelectFilterClick, onEditFilterComplete, toastMessagePop],
  );

  return (
    <Flex data-testid="channel-list-filter-popover-content" direction="column" overflow="hidden" boxShadow="lg">
      {filterPopoverState.view === FilterPopoverView.Edit ? (
        <ChannelListFilterEdit
          currentChannelFilterSelection={currentChannelFilterSelection}
          onFilterApply={handleFilterApply}
          onSavedFiltersClick={handleViewFilterSelect}
          onClearFilterClick={handleClearFilterClick}
          onSaveNewFilterClick={onSaveNewFilter}
          onClosePopover={onClosePopover}
          onShowArchivedToggle={onShowArchivedToggle}
        />
      ) : filterPopoverState.view === FilterPopoverView.Select ? (
        <ChannelListFilterSelect
          channelFilterSelections={channelFilterSelectionsData?.savedChannelFilterSelections}
          currentChannelFilterSelection={currentChannelFilterSelection}
          isQueryLoading={isChannelFilterSelectionsQueryLoading}
          initialFocusRef={initialFocusRef}
          onClearFilterClick={handleClearFilterClick}
          onEditFilterClick={handleViewFilterEditSaved}
          onSelectFilterClick={handleSelectFilterClick}
          onDeleteFilterClick={onDeleteFilter}
          onViewFilterEdit={handleViewFilterEdit}
          onClosePopover={onClosePopover}
        />
      ) : (
        <ChannelListFilterEditSaved
          filterPopoverEditState={filterPopoverEditState}
          onFilterDelete={onDeleteFilter}
          onFilterEditCancel={handleViewFilterSelect}
          onFilterUpdate={handleFilterUpdate}
          onSetFilterPopoverEditState={onSetFilterPopoverEditState}
          onShowArchivedToggle={handleSavedFilterShowArchivedToggle}
        />
      )}
    </Flex>
  );
};

export default ChannelListFilterPopoverContent;
